import { Component, DestroyRef, inject, OnDestroy, OnInit, Renderer2, ViewEncapsulation } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { MatDialog } from "@angular/material/dialog";
import { ActivatedRouteSnapshot, ActivationEnd, Data, NavigationEnd, Router } from "@angular/router";
import { filterNilValue } from "@datorama/akita";
import { NiceMediaWatcherService, NiceNavigationService } from "@recursyve/nice-ui-kit.v2";
import { combineLatest, forkJoin } from "rxjs";
import { filter, map, switchMap, take, tap, throttleTime, withLatestFrom } from "rxjs/operators";
import { FlagsmithService } from "../../flagsmith/services/flagsmith.service";
import { NavigationService } from "../../navigation/navigation.service";
import { ServiceWorkerService } from "../../service-worker/service-worker.service";
import { AppQuery } from "../../store/app.query";
import { AppService } from "../../store/app.service";
import { CitizenIntroductionComponent } from "./citizen-introduction/citizen-introduction.component";
import { DashboardQuery } from "./store/dashboard.query";
import { DashboardService } from "./store/dashboard.service";

@Component({
    selector: "nerri-dashboard",
    standalone: false,
    templateUrl: "dashboard.template.html",
    styleUrls: ["dashboard.style.scss"],
    encapsulation: ViewEncapsulation.None
})
export class DashboardComponent implements OnInit, OnDestroy {
    public loading$ = this.query.selectLoading();
    public currentAccount$ = this.appQuery.selectCurrentAccount();
    public shouldShowIntroduction$ = this.query.shouldShowIntroduction();
    public shouldShowNewSteps$ = this.query.shouldShowNewSteps();

    public useOwnerHomeCustomNav = false;
    public shouldHideBreadcrumb = false;

    protected readonly drawerPortal$ = this.dashboardService.getDrawerPortal();
    protected readonly isSmallScreen$ = this.mediaWatcher
        .asObservable()
        .pipe(map(() => this.mediaWatcher.isActive("lt-lg")));

    private readonly destroyRef = inject(DestroyRef);

    constructor(
        private niceNavigationService: NiceNavigationService,
        private query: DashboardQuery,
        private appService: AppService,
        private appQuery: AppQuery,
        private dashboardService: DashboardService,
        private dialog: MatDialog,
        private router: Router,
        private serviceWorkerService: ServiceWorkerService,
        private navigationService: NavigationService,
        private mediaWatcher: NiceMediaWatcherService,
        private renderer: Renderer2
    ) {}

    public ngOnInit(): void {
        this.serviceWorkerService.listen();

        const ownerHomeVersion = FlagsmithService.getValue("owner_home");
        this.useOwnerHomeCustomNav = ownerHomeVersion === "v3";

        const initialData = this.getInitialData();
        if (initialData["hideBreadcrumb"]) {
            this.shouldHideBreadcrumb = true;
        }

        const routerData$ = this.router.events.pipe(
            filter(event => event instanceof ActivationEnd),
            throttleTime(0),
            map(e => (e as ActivationEnd).snapshot.data)
        );

        this.router.events
            .pipe(
                filter(event => event instanceof NavigationEnd),
                map(navEnd => (navEnd as NavigationEnd).urlAfterRedirects),
                withLatestFrom(routerData$)
            )
            .subscribe(([, data]) => {
                this.shouldHideBreadcrumb = !!data["hideBreadcrumb"];
            });

        this.currentAccount$
            .pipe(
                filterNilValue(),
                filter(account => account.role !== "owner"),
                switchMap(account =>
                    forkJoin([
                        this.dashboardService.loadModules().pipe(map(modules => ({ account, modules }))),
                        this.dashboardService.loadUnits()
                    ])
                ),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe(([{ account, modules }]) => {
                this.navigationService.setNavigationFromAccounts(account, modules);
            });

        this.appService.loadCurrentAccount().subscribe();
        this.dashboardService.loadLocations().pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
    }

    public ngOnDestroy(): void {
        this.serviceWorkerService.close();
    }

    public toggleNavigation(name: string): void {
        const navigation = this.niceNavigationService.getComponent(name);
        if (navigation) {
            navigation.toggle();
        }
    }

    private openIntroductionDialog(): void {
        combineLatest([
            this.currentAccount$,
            this.shouldShowIntroduction$,
            this.shouldShowNewSteps$,
            this.isSmallScreen$
        ])
            .pipe(
                take(1),
                filter(
                    ([currentAccount, shouldShowIntro, shouldShowNewSteps, isSmallScreen]) =>
                        currentAccount?.role === "owner" && (shouldShowIntro || shouldShowNewSteps) && !isSmallScreen
                ),
                tap(() => this.renderer.setStyle(document.body, "overflow", "hidden")),
                switchMap(() => {
                    const dialogRef = this.dialog.open(CitizenIntroductionComponent, {
                        maxWidth: "100vw",
                        maxHeight: "100vh",
                        height: "100%",
                        width: "100%",
                        panelClass: "citizen-introduction-panel"
                    });

                    return dialogRef.afterClosed();
                }),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe(() => {
                this.renderer.setStyle(document.body, "overflow", "visible");
                this.dashboardService.completeIntroduction();
            });
    }

    private getInitialData(): Data {
        const state = this.lastRouterChild(this.router.routerState.snapshot.root);
        return state.data;
    }

    private lastRouterChild(route: ActivatedRouteSnapshot): ActivatedRouteSnapshot {
        if (route.firstChild) {
            return this.lastRouterChild(route.firstChild);
        } else {
            return route;
        }
    }
}
